<script setup>
import { computed, ref, defineEmits, defineProps } from 'vue';
import { pushToDataLayer } from '@/ui/scripts/analytics/analytics.methods';

const focusEl = ref(null);
defineExpose({ focusEl });

const emit = defineEmits(['click']);
const props = defineProps({
  activeIndex: {
    required: true,
    type: Number,
  },
  id: {
    required: true,
  },
  itemIndex: {
    required: true,
    type: Number,
  },
  region: {
    required: false,
    type: String,
  },
});
const isActive = computed(() => props.activeIndex === props.itemIndex);
const isHidden = computed(() => (isActive.value === false ? true : null));

function handleClick() {
  emit('click', props.itemIndex);

  pushToDataLayer(
    'projectMapProjectList_tracking',
    'projectMapProjectList_action',
    'projectMapProjectList_label',
    `Click ${props.region}`,
    isActive.value ? 'Close' : 'Open',
  );
}
</script>

<template>
  <li class="c-projects-listitem" role="listitem">
    <h3 class="c-projects-listitem__title">
      <button
        @click="handleClick"
        :aria-expanded="isActive"
        :aria-controls="id"
        :id="`trigger-${id}`"
        class="c-projects-listitem__button"
        ref="focusEl"
        type="button"
      >
        <slot name="label" />
        <svg focusable="false" aria-hidden="true">
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#down-arrow"></use>
        </svg>
      </button>
    </h3>
    <div :hidden="isHidden" :id="id" class="c-projects-listitem__content">
      <slot name="content" />
    </div>
  </li>
</template>

<style lang="scss">
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/mixins/buttons';
@import '@/ui/styles/mixins/focus';
@import '@/ui/styles/settings/colors';
@import '@/ui/styles/settings/fonts';
@import '@/ui/styles/settings/units';

.c-projects-listitem {
  margin-bottom: rem(4px);
  list-style: none;
}

.c-projects-listitem__button {
  @include iconButton(rem(15px));
  @include removeDefaultButtonStyling();
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: rem(13px) rem(24px);
  width: 100%;
  background-color: rgba($color-brand-three, 0.15);
  color: $color-brand-one;
  font-size: rem(16px);
  font-weight: $font-weight-bold;
  line-height: (30 / 16);
  cursor: pointer;

  &[aria-expanded='true'] > svg {
    transform: rotate(180deg);
  }
}
</style>
